/**
 * @module figureImagePlugin
 * @description Custom Milkdown plugin for rendering images with figure and figcaption
 *
 * This plugin:
 * 1. Renders images as <figure><img><figcaption></figcaption></figure>
 * 2. Resolves filename references to base64 data URLs for display
 * 3. Shows the alt text as a visible caption below the image
 */

import { $view } from "@milkdown/kit/utils";
import { imageSchema } from "@milkdown/kit/preset/commonmark";
import type { Node as ProseMirrorNode } from "@milkdown/kit/prose/model";
import type {
  EditorView,
  NodeView,
  NodeViewConstructor,
} from "@milkdown/kit/prose/view";
import {
  resolveImageUrl,
  setImageCache,
} from "../services/imageResolverService";
import { images } from "../stores/appStore";
import type { Unsubscriber } from "svelte/store";

/**
 * Convert HTML to Markdown (for caption editing)
 * Supports: bold (**), italic (*), and combinations
 */
function htmlToMarkdown(html: string): string {
  // Create a temporary element to parse HTML
  const temp = document.createElement("div");
  temp.innerHTML = html;

  function processNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      const children = Array.from(el.childNodes).map(processNode).join("");

      if (tag === "b" || tag === "strong") {
        return `**${children}**`;
      }
      if (tag === "i" || tag === "em") {
        return `*${children}*`;
      }
      // For other elements, just return children
      return children;
    }

    return "";
  }

  return Array.from(temp.childNodes).map(processNode).join("");
}

/**
 * Convert Markdown to HTML (for caption display)
 * Supports: bold (**), italic (*), and combinations
 */
function markdownToHtml(markdown: string): string {
  let html = markdown;
  // Bold: **text** -> <strong>text</strong>
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Italic: *text* -> <em>text</em> (but not inside **)
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
  return html;
}

/**
 * Custom NodeView for rendering images as figures with captions
 */
class FigureImageView implements NodeView {
  dom: HTMLElement;
  contentDOM?: HTMLElement;
  private img: HTMLImageElement;
  private figcaption: HTMLElement;
  private node: ProseMirrorNode;
  private view: EditorView;
  private getPos: () => number | undefined;
  private unsubscribe: Unsubscriber;

  constructor(
    node: ProseMirrorNode,
    view: EditorView,
    getPos: () => number | undefined,
  ) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;

    // Create figure element
    this.dom = document.createElement("figure");
    this.dom.className = "preparo-figure";

    // Create image element
    this.img = document.createElement("img");
    this.updateImage(node);

    // Create figcaption element (editable)
    this.figcaption = document.createElement("figcaption");
    this.figcaption.className = "preparo-figcaption";
    this.figcaption.contentEditable = "true";
    this.updateCaption(node);

    // Handle caption editing
    this.figcaption.addEventListener(
      "input",
      this.handleCaptionInput.bind(this),
    );
    this.figcaption.addEventListener(
      "keydown",
      this.handleCaptionKeydown.bind(this),
    );

    // Assemble the figure
    this.dom.appendChild(this.img);
    this.dom.appendChild(this.figcaption);

    // Make the figure draggable
    this.dom.draggable = true;

    // Subscribe to images store to re-resolve when images are loaded
    this.unsubscribe = images.subscribe((imgs) => {
      setImageCache(imgs);
      this.updateImage(this.node);
    });
  }

  /**
   * Handle caption input to update the node's alt attribute
   * Converts HTML formatting to Markdown (Pandoc supports markdown in alt)
   */
  private handleCaptionInput(): void {
    const pos = this.getPos();
    if (pos === undefined) return;

    // Convert HTML (from contentEditable) to Markdown for alt attribute
    const newAlt = htmlToMarkdown(this.figcaption.innerHTML);

    // Only update if the text actually changed
    if (newAlt === this.node.attrs.alt) return;

    const { state, dispatch } = this.view;
    const tr = state.tr.setNodeMarkup(pos, undefined, {
      ...this.node.attrs,
      alt: newAlt,
      title: this.figcaption.textContent || "", // title stays plain text
    });
    dispatch(tr);
  }

  /**
   * Handle keydown in caption
   * - Prevent Enter from creating new paragraph
   * - Support formatting shortcuts (Cmd+B, Cmd+I)
   */
  private handleCaptionKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      return;
    }

    // Handle formatting shortcuts using execCommand
    const isMod = event.metaKey || event.ctrlKey;
    if (isMod) {
      if (event.key === "b" || event.key === "B") {
        event.preventDefault();
        document.execCommand("bold");
      } else if (event.key === "i" || event.key === "I") {
        event.preventDefault();
        document.execCommand("italic");
      }
    }
  }

  /**
   * Update the image src, resolving filename references to base64
   */
  private updateImage(node: ProseMirrorNode): void {
    const src = node.attrs.src as string;
    const alt = (node.attrs.alt as string) || "";
    const title = (node.attrs.title as string) || alt;

    // Resolve filename references (like "figure-1-1.jpg") to base64 data URLs
    const resolvedSrc = resolveImageUrl(src);

    // Only update if the src actually changed (avoid infinite loops)
    if (this.img.src !== resolvedSrc) {
      this.img.src = resolvedSrc;
    }

    this.img.alt = alt;
    this.img.title = title;
    this.img.className = "preparo-figure-image";

    // Add loading indicator for large images
    this.img.loading = "lazy";
  }

  /**
   * Update the figcaption with alt text
   * Skip content update if the figcaption is being edited (has focus)
   * Converts Markdown to HTML for display
   */
  private updateCaption(node: ProseMirrorNode): void {
    const alt = (node.attrs.alt as string) || "";

    // Don't update content if figcaption is focused (user is editing)
    const isFocused = document.activeElement === this.figcaption;

    if (alt) {
      if (!isFocused) {
        // Convert Markdown to HTML for display
        this.figcaption.innerHTML = markdownToHtml(alt);
      }
      this.figcaption.style.display = "block";
    } else {
      if (!isFocused) {
        this.figcaption.innerHTML = "";
      }
      this.figcaption.style.display = "block"; // Keep visible for empty placeholder
    }
  }

  /**
   * Handle node updates
   */
  update(node: ProseMirrorNode): boolean {
    if (node.type.name !== "image") {
      return false;
    }

    this.node = node;
    this.updateImage(node);
    this.updateCaption(node);

    return true;
  }

  /**
   * Don't track selection inside
   */
  selectNode(): void {
    this.dom.classList.add("ProseMirror-selectednode");
  }

  deselectNode(): void {
    this.dom.classList.remove("ProseMirror-selectednode");
  }

  /**
   * Stop events from bubbling, but allow events in the figcaption for editing
   */
  stopEvent(event: Event): boolean {
    // Allow all events in the figcaption for editing
    if (
      event.target === this.figcaption ||
      this.figcaption.contains(event.target as Node)
    ) {
      return true;
    }
    return event.type === "mousedown";
  }

  /**
   * Clean up
   */
  destroy(): void {
    // Unsubscribe from images store
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }
}

/**
 * Factory function for creating the NodeView
 */
const createFigureImageView: NodeViewConstructor = (node, view, getPos) => {
  return new FigureImageView(node, view, getPos as () => number | undefined);
};

/**
 * The Milkdown plugin that registers the custom image NodeView
 */
export const figureImagePlugin = $view(
  imageSchema.node,
  () => createFigureImageView,
);

/**
 * CSS styles for the figure/figcaption (to be added to theme or global CSS)
 * These are exported so they can be included in the theme
 */
export const figureImageStyles = `
  /* Figure container */
  .preparo-figure {
    display: block;
    margin: 1.5rem auto;
    max-width: 100%;
    text-align: center;
  }

  /* Image inside figure */
  .preparo-figure-image {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  /* Figcaption styling */
  .preparo-figcaption {
    margin-top: 0.75rem;
    padding: 0 1rem;
    font-size: 0.875rem;
    font-style: italic;
    color: var(--text-secondary, #6b7280);
    line-height: 1.4;
  }

  /* Selected state */
  .preparo-figure.ProseMirror-selectednode {
    outline: 2px solid var(--accent-color, #3b82f6);
    outline-offset: 4px;
    border-radius: 8px;
  }

  /* Dark mode */
  @media (prefers-color-scheme: dark) {
    .preparo-figure-image {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }

    .preparo-figcaption {
      color: var(--text-secondary, #9ca3af);
    }
  }
`;

export default figureImagePlugin;
